// CustomerManagerStats.tsx
import React, { useCallback, useEffect, useState } from "react";
import {
  BarChart3,
  CalendarDays,
  Package,
  Image as ImageIcon,
  HardDrive,
  Bell,
  CreditCard,
  CheckCircle2,
  RefreshCw,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
} from "lucide-react";
import { useSelector } from "react-redux";
import type { RootState } from "../store/store";
import { API_V1_BASE_URL } from "../config/api";
import { demoData } from "../mocks/demoData";

// ---- Mock data ---------------------------------------------------------
// Storage, alerts and activity are mock for now. When the real endpoints exist,
// replace these with the API response (same shape) and nothing else changes.

// Storage and alerts come from the shared demo data (src/mocks/simbDemoData.json)
const MOCK_STORAGE = demoData.stats.storage;

interface AlertItem {
  id: string;
  level: "warning" | "info";
  message: string;
  icon: React.ElementType;
}

const ALERT_ICONS: Record<string, React.ElementType> = {
  assets: ImageIcon,
  billing: CreditCard,
};

const MOCK_ALERTS: AlertItem[] = demoData.stats.alerts.map((a) => ({
  id: a.id,
  level: a.level,
  message: a.message,
  icon: ALERT_ICONS[a.icon] ?? ImageIcon,
}));

// ---- Activity mock -----------------------------------------------------
// "Created" counts per month for Events, Products and Assets. The generator is
// deterministic, so the same month always shows the same numbers. Replace
// mockCreated() with an API call such as /activity?year=2026&month=9 later.

type EntryKind = "events" | "products" | "assets";

const ACTIVITY_MONTHS = 5; // how many months the line chart shows together
const MONTH_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const KIND_SEED: Record<EntryKind, number> = {
  events: 1,
  products: 2,
  assets: 3,
};
const KIND_RANGE: Record<EntryKind, [number, number]> = {
  events: [6, 26],
  products: [4, 20],
  assets: [18, 70],
};

const seeded = (seed: number) => {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
};

/** How many entries of this kind were created in the given month (mock). */
const mockCreated = (year: number, month: number, kind: EntryKind) => {
  const [lo, hi] = KIND_RANGE[kind];
  return (
    lo +
    Math.floor(
      seeded(year * 1000 + month * 10 + KIND_SEED[kind]) * (hi - lo + 1),
    )
  );
};

const mockMonthTotal = (year: number, month: number) =>
  mockCreated(year, month, "events") +
  mockCreated(year, month, "products") +
  mockCreated(year, month, "assets");

interface ActivityPoint {
  label: string;
  count: number;
}

/** The last ACTIVITY_MONTHS months, ending with the current month. */
const buildActivity = (): ActivityPoint[] => {
  const now = new Date();
  const points: ActivityPoint[] = [];
  for (let i = ACTIVITY_MONTHS - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    points.push({
      label: MONTH_SHORT[d.getMonth()],
      count: mockMonthTotal(d.getFullYear(), d.getMonth()),
    });
  }
  return points;
};

// ---- Types for the live counts API --------------------------------------

interface CountBlock {
  total?: number;
  active?: number;
  upcoming?: number;
  ready?: number;
  processing?: number;
}

interface DashboardStatsResponse {
  events?: CountBlock;
  products?: CountBlock;
  assets?: CountBlock;
}

// ---- Theme (inline on purpose: the global theme restyles Tailwind colours) --

const ACCENT = "#BE3455";
const ACCENT_TINT = "#FCE7EC";
const ACCENT_TRACK = "#F7DCE3";
const CARD_BORDER = "#E2E8F0";
const CHART_COLOR = "#3B82F6"; // blue, as in the reference. Use ACCENT for the rose theme.

interface KindMeta {
  key: EntryKind;
  label: string;
  icon: React.ElementType;
  color: string;
  tint: string;
}

const KINDS: KindMeta[] = [
  {
    key: "events",
    label: "Events",
    icon: CalendarDays,
    color: "#6366F1",
    tint: "#EEF2FF",
  },
  {
    key: "products",
    label: "Products",
    icon: Package,
    color: "#10B981",
    tint: "#ECFDF5",
  },
  {
    key: "assets",
    label: "Assets",
    icon: ImageIcon,
    color: "#F59E0B",
    tint: "#FFFBEB",
  },
];

// ---- Small building blocks ----------------------------------------------

const Panel: React.FC<{
  children: React.ReactNode;
  dashed?: boolean;
  style?: React.CSSProperties;
}> = ({ children, dashed = false, style }) => (
  <div
    className="rounded-2xl p-4"
    style={{
      backgroundColor: dashed ? "#FFFBFC" : "#FFFFFF",
      border: `1px ${dashed ? "dashed" : "solid"} ${dashed ? ACCENT : CARD_BORDER}`,
      boxShadow: dashed ? "none" : "0 6px 20px -12px rgba(15, 23, 42, 0.2)",
      ...style,
    }}
  >
    {children}
  </div>
);

const SectionHeader: React.FC<{
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  badge?: string;
}> = ({ icon, title, subtitle, badge }) => (
  <div className="flex items-start gap-3 mb-3">
    <span
      className="flex items-center justify-center rounded-xl flex-shrink-0"
      style={{
        width: 40,
        height: 40,
        backgroundColor: ACCENT_TINT,
        color: ACCENT,
      }}
    >
      {icon}
    </span>
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2">
        <h3 className="text-base font-semibold" style={{ color: "#0F172A" }}>
          {title}
        </h3>
        {badge && (
          <span
            className="text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5"
            style={{ backgroundColor: ACCENT_TINT, color: ACCENT }}
          >
            {badge}
          </span>
        )}
      </div>
      {subtitle && (
        <p className="text-sm" style={{ color: "#64748B" }}>
          {subtitle}
        </p>
      )}
    </div>
  </div>
);

interface CountCardProps {
  icon: React.ReactNode;
  label: string;
  value: number | null;
  hint: string;
  loading: boolean;
}

const CountCard: React.FC<CountCardProps> = ({
  icon,
  label,
  value,
  hint,
  loading,
}) => (
  <div
    className="rounded-xl px-4 py-3"
    style={{ border: `1px solid ${CARD_BORDER}`, backgroundColor: "#FFFFFF" }}
  >
    <div className="flex items-center justify-between">
      <span className="text-sm font-medium" style={{ color: "#64748B" }}>
        {label}
      </span>
      <span style={{ color: ACCENT }}>{icon}</span>
    </div>
    <div className="mt-1 flex items-baseline gap-3">
      <p
        className="text-3xl font-bold tracking-tight"
        style={{ color: "#0F172A" }}
      >
        {loading ? (
          <span
            className="inline-block rounded-md animate-pulse"
            style={{ width: 48, height: 30, backgroundColor: "#E2E8F0" }}
          />
        ) : value === null ? (
          "—"
        ) : (
          value.toLocaleString()
        )}
      </p>
      <p className="text-xs" style={{ color: "#94A3B8" }}>
        {hint}
      </p>
    </div>
  </div>
);

// ---- Storage: 3D cylinder gauge (plain SVG) -------------------------------

const StorageCylinder: React.FC<{ usedPct: number; low: boolean }> = ({
  usedPct,
  low,
}) => {
  const base = low ? "#DC2626" : "#BE3455";
  const dark = low ? "#991B1B" : "#8F2440";
  const light = low ? "#F87171" : "#E0607F";

  const cx = 60;
  const rx = 42;
  const ry = 13;
  const topY = 28;
  const botY = 172;
  const left = cx - rx;
  const right = cx + rx;
  const h = botY - topY;
  const fillH = (Math.min(100, Math.max(0, usedPct)) / 100) * h;
  const fillTop = botY - fillH;

  const bodyPath = `M${left},${topY} L${left},${botY} A${rx},${ry} 0 0 0 ${right},${botY} L${right},${topY}`;

  return (
    <svg
      viewBox="0 0 120 200"
      width="120"
      height="200"
      role="img"
      aria-label={`${usedPct}% of storage used`}
    >
      <defs>
        <linearGradient id="cylTrack" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#F0C9D4" />
          <stop offset="45%" stopColor="#FDF2F5" />
          <stop offset="100%" stopColor="#EBBFCB" />
        </linearGradient>
        <linearGradient id="cylFill" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={dark} />
          <stop offset="40%" stopColor={base} />
          <stop offset="100%" stopColor={dark} />
        </linearGradient>
      </defs>

      {/* Empty body */}
      <path d={`${bodyPath} Z`} fill="url(#cylTrack)" />

      {/* Used portion */}
      {fillH > 0 && (
        <>
          <path
            d={`M${left},${fillTop} L${left},${botY} A${rx},${ry} 0 0 0 ${right},${botY} L${right},${fillTop} Z`}
            fill="url(#cylFill)"
          />
          <ellipse cx={cx} cy={fillTop} rx={rx} ry={ry} fill={light} />
        </>
      )}

      {/* Top rim */}
      <ellipse
        cx={cx}
        cy={topY}
        rx={rx}
        ry={ry}
        fill="#FFFFFF"
        fillOpacity="0.55"
        stroke="#E2B7C3"
        strokeWidth="1.5"
      />

      {/* Glass shine */}
      <rect
        x={left + 8}
        y={topY + 6}
        width="7"
        height={h - 10}
        rx="3.5"
        fill="#FFFFFF"
        fillOpacity="0.35"
      />

      {/* Outline */}
      <path d={bodyPath} fill="none" stroke="#E2B7C3" strokeWidth="1.5" />
    </svg>
  );
};

// ---- Activity: line chart (plain SVG) -------------------------------------

const ActivityChart: React.FC<{ data: ActivityPoint[] }> = ({ data }) => {
  if (data.length === 0) {
    return (
      <p className="text-sm" style={{ color: "#94A3B8" }}>
        No activity yet.
      </p>
    );
  }

  const W = 420;
  const H = 200;
  const padX = 32;
  const top = 20;
  const bottom = 36;
  const baseY = H - bottom;
  const max = Math.max(...data.map((d) => d.count), 1);
  const step = data.length > 1 ? (W - padX * 2) / (data.length - 1) : 0;

  const pts = data.map((d, i) => ({
    x: data.length > 1 ? padX + i * step : W / 2,
    y: top + (1 - d.count / max) * (baseY - top),
    label: d.label,
    count: d.count,
  }));

  const line = pts
    .map((p, i) => `${i === 0 ? "M" : "L"}${p.x},${p.y}`)
    .join(" ");
  const area = `${line} L${pts[pts.length - 1].x},${baseY} L${pts[0].x},${baseY} Z`;
  const last = pts[pts.length - 1];

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="w-full h-auto"
      role="img"
      aria-label="Activity chart"
    >
      <defs>
        <linearGradient id="activityFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={CHART_COLOR} stopOpacity="0.28" />
          <stop offset="100%" stopColor={CHART_COLOR} stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* Dashed grid lines */}
      {[1 / 3, 2 / 3, 1].map((f) => {
        const y = baseY - f * (baseY - top);
        return (
          <line
            key={f}
            x1={padX}
            x2={W - padX}
            y1={y}
            y2={y}
            stroke="#CBD5E1"
            strokeWidth="1"
            strokeDasharray="4 6"
            opacity="0.7"
          />
        );
      })}

      {data.length > 1 && <path d={area} fill="url(#activityFill)" />}
      {data.length > 1 && (
        <path
          d={line}
          fill="none"
          stroke={CHART_COLOR}
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}

      {/* Hover targets with native tooltips */}
      {pts.map((p, i) => (
        <circle
          key={`${p.label}-${i}`}
          cx={p.x}
          cy={p.y}
          r="10"
          fill="transparent"
        >
          <title>{`${p.label}: ${p.count}`}</title>
        </circle>
      ))}

      {/* Latest point */}
      <circle
        cx={last.x}
        cy={last.y}
        r="5"
        fill={CHART_COLOR}
        stroke="#FFFFFF"
        strokeWidth="2"
      />

      {/* Month labels (all points are labelled) */}
      {pts.map((p, i) => (
        <text
          key={`${p.label}-${i}`}
          x={p.x}
          y={H - 10}
          textAnchor="middle"
          fontSize="13"
          fill="#94A3B8"
        >
          {p.label}
        </text>
      ))}
    </svg>
  );
};

// ---- Activity: single-month breakdown -------------------------------------

const DeltaBadge: React.FC<{
  cur: number;
  prev: number;
  prevLabel: string;
}> = ({ cur, prev, prevLabel }) => {
  if (prev <= 0) {
    return (
      <span className="text-xs" style={{ color: "#94A3B8" }}>
        No data for {prevLabel}
      </span>
    );
  }
  const pct = Math.round(((cur - prev) / prev) * 100);
  const up = pct >= 0;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span
      className="inline-flex items-center gap-1 text-xs font-semibold"
      style={{ color: up ? "#16A34A" : "#DC2626" }}
    >
      <Icon className="w-3.5 h-3.5" />
      {up ? "+" : ""}
      {pct}% vs {prevLabel}
    </span>
  );
};

const MonthBreakdown: React.FC<{ year: number; month: number }> = ({
  year,
  month,
}) => {
  const prevDate = new Date(year, month - 1, 1);
  const prevLabel = MONTH_SHORT[prevDate.getMonth()];

  const rows = KINDS.map((k) => ({
    ...k,
    count: mockCreated(year, month, k.key),
    prev: mockCreated(prevDate.getFullYear(), prevDate.getMonth(), k.key),
  }));
  const total = rows.reduce((sum, r) => sum + r.count, 0);
  const prevTotal = rows.reduce((sum, r) => sum + r.prev, 0);

  return (
    <div>
      {/* Total + comparison with the previous month */}
      <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
        <div>
          <p className="text-sm font-medium" style={{ color: "#64748B" }}>
            Total new entries
          </p>
          <p
            className="text-5xl font-bold tracking-tight"
            style={{ color: "#0F172A" }}
          >
            {total}
          </p>
        </div>
        <DeltaBadge cur={total} prev={prevTotal} prevLabel={prevLabel} />
      </div>

      {/* Stacked share bar */}
      <div
        className="flex w-full overflow-hidden rounded-full mb-6"
        style={{ height: 12, backgroundColor: "#F1F5F9", gap: 2 }}
      >
        {rows.map((r) => (
          <div
            key={r.key}
            className="h-full transition-all duration-500"
            style={{
              width: `${total > 0 ? (r.count / total) * 100 : 0}%`,
              backgroundColor: r.color,
            }}
            title={`${r.label}: ${r.count}`}
          />
        ))}
      </div>

      {/* One card per type */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {rows.map((r) => {
          const Icon = r.icon;
          const share = total > 0 ? Math.round((r.count / total) * 100) : 0;
          return (
            <div
              key={r.key}
              className="rounded-xl p-4"
              style={{
                border: `1px solid ${CARD_BORDER}`,
                backgroundColor: "#FFFFFF",
                borderTop: `4px solid ${r.color}`,
              }}
            >
              <div className="flex items-center justify-between">
                <span
                  className="text-sm font-medium"
                  style={{ color: "#64748B" }}
                >
                  {r.label} created
                </span>
                <span
                  className="flex items-center justify-center rounded-lg"
                  style={{
                    width: 36,
                    height: 36,
                    backgroundColor: r.tint,
                    color: r.color,
                  }}
                >
                  <Icon className="w-5 h-5" />
                </span>
              </div>

              <p
                className="mt-3 text-4xl font-bold tracking-tight"
                style={{ color: "#0F172A" }}
              >
                {r.count}
              </p>

              <div
                className="mt-3 w-full overflow-hidden rounded-full"
                style={{ height: 6, backgroundColor: r.tint }}
              >
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{ width: `${share}%`, backgroundColor: r.color }}
                />
              </div>

              <div className="mt-3 flex items-center justify-between">
                <span className="text-xs" style={{ color: "#94A3B8" }}>
                  {share}% of total
                </span>
                <DeltaBadge cur={r.count} prev={r.prev} prevLabel={prevLabel} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ---- Page ----------------------------------------------------------------

const CustomerManagerStats: React.FC = () => {
  const { token, accessToken, user } = useSelector(
    (state: RootState) => state.auth,
  );

  const storedUser = (() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  })();
  const authToken = accessToken || token || localStorage.getItem("accessToken");
  const tenantId =
    (user as any)?.tenantId ||
    (user as any)?.tenant_id ||
    storedUser?.tenantId ||
    storedUser?.tenant_id;

  const [stats, setStats] = useState<DashboardStatsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Activity filter: month -1 means "Last 5 months" (line chart)
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();
  const [activityYear, setActivityYear] = useState<number>(currentYear);
  const [activityMonth, setActivityMonth] = useState<number>(-1);
  const yearOptions = [currentYear, currentYear - 1, currentYear - 2];
  const monthSelected = activityMonth !== -1;

  const fetchStats = useCallback(
    async (isManual = false) => {
      try {
        if (isManual) setRefreshing(true);
        setError(null);

        if (!authToken) {
          throw new Error("No authentication token found. Please login again.");
        }
        if (!tenantId) {
          throw new Error("No tenant found for this account.");
        }

        const res = await fetch(
          `${API_V1_BASE_URL}/customer-manager/dashboard-stats?tenant_id=${tenantId}`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
              "Content-Type": "application/json",
            },
          },
        );

        if (!res.ok) {
          const errData = await res.json().catch(() => null);
          throw new Error(
            errData?.message || "Failed to fetch platform statistics",
          );
        }

        const json = await res.json();
        setStats(json.data as DashboardStatsResponse);
        setLastUpdated(new Date());
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to fetch data");
        console.error("Error fetching customer manager stats:", err);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [authToken, tenantId],
  );

  // Initial load, then refresh quietly every 60 seconds so the counts stay current.
  useEffect(() => {
    fetchStats();
    const timer = window.setInterval(() => fetchStats(), 60000);
    return () => window.clearInterval(timer);
  }, [fetchStats]);

  // Storage maths (mock data)
  const { usedGb, limitGb } = MOCK_STORAGE;
  const usedPct = Math.min(100, Math.round((usedGb / limitGb) * 100));
  const leftPct = Math.max(0, 100 - usedPct);
  const storageLow = leftPct <= 20;
  const freeGb = Number(Math.max(0, limitGb - usedGb).toFixed(1));

  const alerts: AlertItem[] = [
    ...(storageLow
      ? [
          {
            id: "storage-low",
            level: "warning" as const,
            message: `Only ${leftPct}% of storage is left before your pricing tier changes.`,
            icon: AlertTriangle,
          },
        ]
      : []),
    ...MOCK_ALERTS,
  ];

  const activityData = buildActivity();

  const filterSelectStyle: React.CSSProperties = {
    border: `1px solid ${CARD_BORDER}`,
    backgroundColor: "#FFFFFF",
    color: "#0F172A",
    borderRadius: 9999,
    padding: "6px 14px",
    fontSize: 14,
    fontWeight: 500,
    cursor: "pointer",
  };

  const events = stats?.events;
  const products = stats?.products;
  const assets = stats?.assets;

  return (
    <div className="w-full">
     

      {/* Platform statistics (live API) */}
      <Panel style={{ marginBottom: 12 }}>
        {error && (
          <div
            className="mb-4 rounded-lg px-4 py-3 text-sm flex items-center justify-between gap-3"
            style={{
              backgroundColor: "#FEF2F2",
              color: "#B91C1C",
              border: "1px solid #FECACA",
            }}
          >
            <span>{error}</span>
            <button
              type="button"
              onClick={() => fetchStats(true)}
              className="font-semibold underline"
            >
              Retry
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <CountCard
            icon={<CalendarDays className="w-5 h-5" />}
            label="Events"
            value={events?.total ?? null}
            hint={
              events?.upcoming !== undefined
                ? `${events.upcoming} upcoming`
                : "Total events"
            }
            loading={loading}
          />
          <CountCard
            icon={<Package className="w-5 h-5" />}
            label="Products"
            value={products?.total ?? null}
            hint={
              products?.active !== undefined
                ? `${products.active} active`
                : "Total products"
            }
            loading={loading}
          />
          <CountCard
            icon={<ImageIcon className="w-5 h-5" />}
            label="Assets"
            value={assets?.total ?? null}
            hint={
              assets?.ready !== undefined
                ? `${assets.ready} ready${
                    assets.processing ? `, ${assets.processing} processing` : ""
                  }`
                : "Total assets"
            }
            loading={loading}
          />
        </div>
      </Panel>

      {/* Activity + Storage + Alerts: one row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-3">
        {/* Activity overview (mock) */}
        <Panel style={{ height: "100%" }}>
          <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
            <h3
              className="text-base font-semibold"
              style={{ color: "#0F172A" }}
            >
              Activity overview
            </h3>

            <div className="flex items-center gap-2">
              <select
                aria-label="Select month"
                value={activityMonth}
                onChange={(e) => setActivityMonth(Number(e.target.value))}
                style={filterSelectStyle}
              >
                <option value={-1}>Last {ACTIVITY_MONTHS} months</option>
                {MONTH_NAMES.map((name, m) => (
                  <option
                    key={name}
                    value={m}
                    disabled={activityYear === currentYear && m > currentMonth}
                  >
                    {name}
                  </option>
                ))}
              </select>
              <select
                aria-label="Select year"
                value={activityYear}
                disabled={!monthSelected}
                onChange={(e) => {
                  const y = Number(e.target.value);
                  setActivityYear(y);
                  // A month that is still in the future for the new year falls back to the overview
                  if (y === currentYear && activityMonth > currentMonth)
                    setActivityMonth(-1);
                }}
                style={{
                  ...filterSelectStyle,
                  opacity: monthSelected ? 1 : 0.5,
                  cursor: monthSelected ? "pointer" : "not-allowed",
                }}
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {monthSelected ? (
            <MonthBreakdown year={activityYear} month={activityMonth} />
          ) : (
            <ActivityChart data={activityData} />
          )}
        </Panel>

                {/* Storage (mock): percentage on the left, cylinder on the right */}
        <div>
          <Panel style={{ height: "100%" }}>
            <SectionHeader
              icon={<HardDrive className="w-5 h-5" />}
              title="Storage"
              subtitle={`${usedGb} of ${limitGb} GB used`}
            />

            <div className="flex items-center justify-between gap-4 px-2 py-1">
              {/* Left: percentage + used/free */}
              <div>
                <p
                  className="text-5xl font-bold tracking-tight"
                  style={{ color: storageLow ? "#B91C1C" : "#0F172A" }}
                >
                  {leftPct}%
                </p>
                <p className="text-sm font-medium mb-4" style={{ color: "#64748B" }}>
                  storage left
                </p>

                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm" style={{ color: "#334155" }}>
                    <span
                      className="rounded-full flex-shrink-0"
                      style={{ width: 10, height: 10, backgroundColor: storageLow ? "#DC2626" : ACCENT }}
                    />
                    <span>
                      Used <strong style={{ color: "#0F172A" }}>{usedGb} GB</strong>
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm" style={{ color: "#334155" }}>
                    <span
                      className="rounded-full flex-shrink-0"
                      style={{ width: 10, height: 10, backgroundColor: "#F0C9D4" }}
                    />
                    <span>
                      Free <strong style={{ color: "#0F172A" }}>{freeGb} GB</strong>
                    </span>
                  </div>
                </div>
              </div>

              {/* Right: cylinder */}
              <StorageCylinder usedPct={usedPct} low={storageLow} />
            </div>
          </Panel>
        </div>

        {/* Alerts (mock) */}

        <Panel style={{ height: "100%" }}>
          <SectionHeader
            icon={<Bell className="w-5 h-5" />}
            title="Alerts"
            subtitle="Any warnings for the account"
          />

          {alerts.length === 0 ? (
            <div
              className="flex items-center gap-2 text-sm"
              style={{ color: "#16A34A" }}
            >
              <CheckCircle2 className="w-4 h-4" />
              No warnings. Everything looks good.
            </div>
          ) : (
            <ul className="space-y-2">
              {alerts.map((alert) => {
                const AlertIcon = alert.icon;
                const isWarning = alert.level === "warning";
                return (
                  <li
                    key={alert.id}
                    className="flex items-start gap-3 rounded-lg px-3 py-2.5 text-sm"
                    style={{
                      backgroundColor: isWarning ? "#FEF2F2" : "#F8FAFC",
                      border: `1px solid ${isWarning ? "#FECACA" : CARD_BORDER}`,
                      color: "#334155",
                    }}
                  >
                    <span
                      className="flex flex-shrink-0 items-center justify-center rounded-lg"
                      style={{
                        width: 28,
                        height: 28,
                        backgroundColor: isWarning ? "#FEE2E2" : "#E2E8F0",
                        color: isWarning ? "#DC2626" : "#64748B",
                      }}
                    >
                      <AlertIcon className="w-4 h-4" />
                    </span>
                    <span className="pt-1">{alert.message}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </Panel>
      </div>

      {/* Billing: coming soon */}
      <Panel dashed>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <span
              className="flex items-center justify-center rounded-xl flex-shrink-0"
              style={{
                width: 40,
                height: 40,
                backgroundColor: ACCENT_TINT,
                color: ACCENT,
              }}
            >
              <CreditCard className="w-5 h-5" />
            </span>
            <div>
              <h3
                className="text-base font-semibold"
                style={{ color: "#0F172A" }}
              >
                Billing
              </h3>
              <p className="text-sm" style={{ color: "#64748B" }}>
                Invoices, payment methods and plan changes will live here.
              </p>
            </div>
          </div>
          <span
            className="text-xs font-bold uppercase tracking-wider rounded-full px-4 py-1.5"
            style={{
              backgroundColor: ACCENT_TINT,
              color: ACCENT,
              border: `1px solid ${ACCENT}`,
            }}
          >
            Coming soon
          </span>
        </div>
      </Panel>
    </div>
  );
};

export default CustomerManagerStats;
